import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import {
  checkServiceEligibility,
  taxServiceCategories
} from '../../../../mockData/orgDocIndex';

/**
 * Tax Service Card Component
 * Displays a single tax service with pricing and request action
 *
 * @param {Object} props
 * @param {Object} props.service - Service data
 * @param {Function} props.onRequest - Request service handler
 * @param {string} props.organizationId - Organization ID for eligibility check
 */
const TaxServiceCard = ({ service, onRequest, organizationId }) => {
  // Check eligibility
  const eligibility = checkServiceEligibility(service.code, organizationId);

  // Get category color
  const category = taxServiceCategories.find(c => c.code === service.categoryCode);
  const categoryColor = category?.color || '#666';

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      {/* Category indicator */}
      <Box
        sx={{
          height: 4,
          bgcolor: categoryColor
        }}
      />

      {/* Popular badge */}
      {service.isPopular && (
        <Chip
          icon={<StarIcon sx={{ fontSize: 14 }} />}
          label="Popular"
          size="small"
          color="warning"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            height: 22,
            fontSize: '0.7rem'
          }}
        />
      )}

      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Category chip */}
        <Chip
          label={category?.name || service.categoryCode}
          size="small"
          sx={{
            mb: 1.5,
            height: 20,
            fontSize: '0.65rem',
            bgcolor: `${categoryColor}20`,
            color: categoryColor,
            fontWeight: 600
          }}
        />

        {/* Service name */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {service.name}
        </Typography>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {service.description}
        </Typography>

        {/* Details */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {service.estimatedDays} days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {service.requiredDocuments.length} docs required
            </Typography>
          </Box>
        </Box>

        {/* Pricing */}
        <Divider sx={{ my: 1.5 }} />
        <Box>
          {service.govtCharge > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Government Fee
              </Typography>
              <Typography variant="caption">
                {formatCurrency(service.govtCharge)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Service Fee
            </Typography>
            <Typography variant="caption">
              {formatCurrency(service.serviceCharge)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              VAT (5%)
            </Typography>
            <Typography variant="caption">
              {formatCurrency(service.taxOnServiceCharge)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight="bold">
              Total
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {formatCurrency(service.totalCharge)}
            </Typography>
          </Box>
        </Box>

        {/* Prerequisites warning */}
        {service.prerequisites.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Requires: {service.prerequisites.join(', ')}
            </Typography>
          </Box>
        )}

        {/* Eligibility warning */}
        {!eligibility.eligible && (
          <Box
            sx={{
              mt: 2,
              p: 1,
              bgcolor: 'warning.lighter',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <BlockIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.dark">
              {eligibility.reason}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {eligibility.eligible ? (
          <Button
            variant="contained"
            fullWidth
            onClick={onRequest}
          >
            Request Service
          </Button>
        ) : (
          <Tooltip title={eligibility.reason}>
            <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                disabled
              >
                Not Available
              </Button>
            </span>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

export default TaxServiceCard;
